import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Plus, ArrowRight, Sparkles, Building2, Search, FileSignature, FileDown, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { SELLER_STAGES } from "../../lib/stages";
import { FUND_MODEL_LABEL, SEARCH_STAGE_LABEL } from "../../lib/taxonomy";
import { MatchScore } from "../../components/MatchBadge";
import SignerProfileCard from "../../components/SignerProfileCard";
import { MandateDialog } from "../../components/MandateDialog";
import { t } from "../../lib/i18n";

export default function SellerDashboard() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [welcome, setWelcome] = useState("");
  const [welcomeCta, setWelcomeCta] = useState(null);
  const [interests, setInterests] = useState([]);
  const [sellerMatches, setSellerMatches] = useState(null);
  const [ndas, setNdas] = useState([]);
  const [mandateSig, setMandateSig] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/companies/mine");
        setCompanies(r.data || []);
      } finally {
        setLoading(false);
      }
      api.get("/matches/seller")
        .then((m) => setSellerMatches(m.data || []))
        .catch(() => setSellerMatches([]));
      api.get("/nda/received")
        .then((n) => setNdas(n.data || []))
        .catch(() => setNdas([]));
      try {
        const w = await api.post("/ai/welcome");
        setWelcome(w.data.message);
        setWelcomeCta(w.data?.cta_path ? { path: w.data.cta_path, label: w.data.cta_label } : null);
      } catch {}
      try {
        const [s, inv] = await Promise.all([
          api.get("/interests/mine").catch(() => ({ data: [] })),
          api.get("/investor-interests/mine").catch(() => ({ data: [] })),
        ]);
        const merged = [
          ...(s.data || []).map((it) => ({ ...it, actor_name: it.searcher_name, actor_role: "Searcher" })),
          ...(inv.data || []).map((it) => ({ ...it, actor_name: it.investor_name, actor_role: "Inversor" })),
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setInterests(merged);
      } catch {}
    })();
  }, []);

  const resolveNda = async (sigId, action) => {
    try {
      const r = await api.post(`/nda/${sigId}/${action}`);
      setNdas((prev) => prev.map((s) => (s.signature_id === sigId ? { ...s, ...r.data } : s)));
      toast.success(t("NDA rechazado"));
    } catch {
      toast.error(t("Error actualizando el NDA"));
    }
  };

  const onMandateAccepted = (updated) => {
    setNdas((prev) => prev.map((s) => (s.signature_id === updated.signature_id ? { ...s, ...updated } : s)));
  };

  const downloadMandatePdf = async (sig) => {
    try {
      const r = await api.get(`/mandate/${sig.mandate_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Mandato_Relevo_${sig.mandate_id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error(t("No se pudo descargar el mandato"));
    }
  };

  const downloadNdaPdf = async (sig) => {
    try {
      const r = await api.get(`/nda/${sig.signature_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NDA_${sig.project_code}_${sig.signature_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("Error descargando el PDF"));
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 space-y-12">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8 fade-up">
            <span className="font-mono-label text-gold">{t("Panel del Vendedor")}</span>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">{t("Hola,")}{" "}{user?.name?.split(" ")[0]}</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              {t("Aquí gestionas tus empresas y avanzas en cada etapa para hacerte visible a searchers e inversores cualificados.")}
            </p>
          </div>
          <div className="lg:col-span-4 fade-up-delay-1">
            <Link to="/seller/companies/new">
              <Button
                className="rounded-none w-full bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12"
                data-testid="seller-new-company-btn"
              >
                <Plus className="w-4 h-4 mr-2" />{" "}{t("Añadir empresa")}
              </Button>
            </Link>
          </div>
        </div>

        {welcome && (
          <div className="hairline p-6 bg-bone fade-up-delay-2 flex items-start gap-4" data-testid="seller-ai-welcome">
            <div className="w-10 h-10 hairline flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <span className="font-mono-label text-gold">{t("Consejo de tu asesor AI · próximo paso")}</span>
              <p className="font-serif-display text-xl mt-1 leading-snug">{welcome}</p>
              {welcomeCta && (
                <Link to={welcomeCta.path} className="font-mono-label text-[#C25F1E] mt-3 inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="seller-ai-cta">
                  {welcomeCta.label} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        )}

        <section>
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif-display text-3xl">{t("Tus empresas")}</h2>
            <span className="font-mono-label text-muted-foreground">{companies.length}{" "}{t("en cartera")}</span>
          </div>

          {loading ? (
            <div className="hairline p-12 text-center bg-card" data-testid="seller-companies-loading">
              <span className="font-mono-label text-muted-foreground animate-pulse">{t("Cargando tus empresas…")}</span>
            </div>
          ) : companies.length === 0 ? (
            <div className="hairline p-12 text-center bg-card">
              <Building2 className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-serif-display text-2xl mb-2">{t("Todavía no has añadido ninguna empresa")}</h3>
              <p className="text-muted-foreground mb-6">{t("Empieza por la información más básica. Te llevamos paso a paso.")}</p>
              <Button
                onClick={() => navigate("/seller/companies/new")}
                className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-11 px-6"
                data-testid="seller-empty-add-btn"
              >
                <Plus className="w-4 h-4 mr-2" />{" "}{t("Añadir mi primera empresa")}
              </Button>
            </div>
          ) : (
            <div className={`grid gap-0 hairline border-r-0 border-b-0 ${companies.length === 1 ? "grid-cols-1 lg:max-w-2xl" : "md:grid-cols-2"}`}>
              {companies.map((co) => (
                <CompanyCard key={co.company_id} co={co} />
              ))}
            </div>
          )}
        </section>

        {ndas.length > 0 && (
          <section data-testid="seller-nda-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="font-mono-label text-gold flex items-center gap-2">
                  <FileSignature className="w-3 h-3" />{" "}{t("Confidencialidad")}
                </span>
                <h2 className="font-serif-display text-3xl mt-1">{t("NDAs recibidos")}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("Aprueba un NDA para dar acceso al Information Memorandum de tu empresa. Al aprobar, aceptarás el Mandato Relevo respecto a ese comprador.")}
                </p>
              </div>
              <span className="font-mono-label text-muted-foreground">
                {ndas.filter((s) => s.seller_approval === "pending").length}{" "}{t("pendientes")}
              </span>
            </div>
            <div className="hairline bg-card">
              {ndas.map((s, i) => (
                <div
                  key={s.signature_id}
                  className={`px-6 py-5 flex flex-wrap items-center gap-4 ${i > 0 ? "hairline-t" : ""}`}
                  data-testid={`nda-row-${s.signature_id}`}
                >
                  <div className="w-11 h-11 hairline flex items-center justify-center bg-[#1E3A5F] text-bone flex-shrink-0">
                    <FileSignature className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-serif-display text-xl">{s.signer_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {s.role === "investor" ? t("Inversor") : t("Searcher")} · {s.signer_email}
                    </div>
                    <div className="font-mono-label text-muted-foreground mt-1" style={{ fontSize: "0.6rem" }}>
                      {s.company_name}{" "}{t("· firmado el")}{" "}{new Date(s.signed_at).toLocaleDateString("es-ES")}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadNdaPdf(s)}
                    className="font-mono-label text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
                    data-testid={`nda-pdf-${s.signature_id}`}
                  >
                    <FileDown className="w-3 h-3" /> PDF
                  </button>
                  {s.seller_approval === "approved" ? (
                    <div className="flex items-center gap-3">
                      {s.mandate_id && (
                        <button
                          type="button"
                          onClick={() => downloadMandatePdf(s)}
                          className="font-mono-label text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
                          data-testid={`mandate-pdf-${s.signature_id}`}
                        >
                          <FileDown className="w-3 h-3" />{" "}{t("Mandato")}
                        </button>
                      )}
                      <span className="font-mono-label bg-forest/10 text-forest border border-forest/30 px-3 py-1.5" data-testid={`nda-status-${s.signature_id}`}>
                        {t("Aprobado")}
                      </span>
                    </div>
                  ) : s.seller_approval === "rejected" ? (
                    <span className="font-mono-label bg-destructive/10 text-destructive border border-destructive/30 px-3 py-1.5" data-testid={`nda-status-${s.signature_id}`}>
                      {t("Rechazado")}
                    </span>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setMandateSig(s)}
                        className="rounded-none text-bone h-9 px-4"
                        style={{ backgroundColor: "#2E6F80" }}
                        data-testid={`nda-approve-${s.signature_id}`}
                      >
                        {t("Aprobar")}
                      </Button>
                      <Button
                        onClick={() => resolveNda(s.signature_id, "reject")}
                        variant="outline"
                        className="rounded-none h-9 px-4 border-destructive text-destructive hover:bg-destructive/5"
                        data-testid={`nda-reject-${s.signature_id}`}
                      >
                        {t("Rechazar")}
                      </Button>
                    </div>
                  )}
                  <SignerProfileCard sig={s} />
                </div>
              ))}
            </div>
            <MandateDialog
              sig={mandateSig}
              open={Boolean(mandateSig)}
              onOpenChange={(o) => { if (!o) setMandateSig(null); }}
              onAccepted={onMandateAccepted}
            />
          </section>
        )}

        {sellerMatches && sellerMatches.some((c) => c.matches.length > 0) && (
          <section data-testid="seller-match-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="font-mono-label text-gold flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />{" "}{t("Motor de match triádico")}
                </span>
                <h2 className="font-serif-display text-3xl mt-1">{t("Searchers que encajan con tus empresas")}</h2>
              </div>
            </div>
            <div className="space-y-6">
              {sellerMatches.filter((c) => c.matches.length > 0).map((c) => (
                <div key={c.company_id} className="hairline bg-card">
                  <div className="px-6 py-4 hairline-b bg-bone flex items-center justify-between">
                    <span className="font-serif-display text-xl">{c.company_name}</span>
                    <span className="font-mono-label text-muted-foreground">{c.matches.length}{" "}{t("candidatos")}</span>
                  </div>
                  {c.matches.map((m, i) => (
                    <div
                      key={m.searcher.user_id}
                      className={`px-6 py-5 flex flex-wrap items-start gap-4 ${i > 0 ? "hairline-t" : ""}`}
                      data-testid={`seller-match-row-${m.searcher.user_id}`}
                    >
                      <div className="w-11 h-11 hairline flex items-center justify-center bg-[#1E3A5F] text-bone flex-shrink-0">
                        <Search className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-[240px]">
                        <div className="font-serif-display text-xl">{m.searcher.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {m.searcher.current_position}
                          {m.searcher.years_experience != null && ` · ${m.searcher.years_experience} años de experiencia`}
                          {m.searcher.education && ` · ${m.searcher.education}`}
                        </div>
                        <div className="font-mono-label text-muted-foreground mt-1" style={{ fontSize: "0.6rem" }}>
                          {FUND_MODEL_LABEL[m.searcher.fund_model] || m.searcher.fund_model}
                          {" · "}
                          {SEARCH_STAGE_LABEL[m.searcher.search_stage] || m.searcher.search_stage}
                          {m.searcher.fund_name && ` · ${m.searcher.fund_name}`}
                        </div>
                        {(m.soft_rationale_seller || m.soft_rationale) && (
                          <p className="text-sm text-muted-foreground italic mt-2 line-clamp-2">"{m.soft_rationale_seller || m.soft_rationale}"</p>
                        )}
                      </div>
                      <MatchScore value={m.total_score} testid={`seller-match-score-${m.searcher.user_id}`} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>
        )}

        {interests.length > 0 && (
          <section data-testid="seller-interest-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="font-mono-label text-gold flex items-center gap-2">
                  <MessageSquare className="w-3 h-3" />{" "}{t("Mensajes de searchers e inversores")}
                </span>
                <h2 className="font-serif-display text-3xl mt-1">{t("Interés recibido")}</h2>
              </div>
              <span className="font-mono-label text-muted-foreground">{interests.length} {interests.length === 1 ? "mensaje" : "mensajes"}</span>
            </div>
            <div className="hairline bg-card">
              {interests.map((it, i) => (
                <div
                  key={it.interest_id}
                  className={`px-6 py-5 flex flex-wrap items-start gap-4 ${i > 0 ? "hairline-t" : ""}`}
                  data-testid={`interest-row-${it.interest_id}`}
                >
                  <div className="w-11 h-11 hairline flex items-center justify-center bg-[#1E3A5F] text-bone flex-shrink-0">
                    {it.actor_role === "Inversor" ? <Building2 className="w-4 h-4" /> : <Search className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-serif-display text-xl">{it.actor_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {it.actor_role}{" "}{t("· interesado en")}{" "}<span className="text-foreground font-medium">{it.company?.name}</span>
                    </div>
                    {it.message ? (
                      <blockquote className="mt-3 hairline bg-bone px-4 py-3 text-sm leading-relaxed" data-testid={`interest-message-${it.interest_id}`}>
                        "{it.message}"
                      </blockquote>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground italic">{t("Sin mensaje adjunto.")}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-mono-label text-gold">{it.status?.toUpperCase()}</span>
                    <span className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>
                      {new Date(it.created_at).toLocaleDateString("es-ES")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}

const CompanyCard = ({ co }) => {
  const stage = SELLER_STAGES.find((s) => s.id === co.stage) || SELLER_STAGES[0];
  const completed = co.completed_stages?.length || 0;
  const pct = Math.round((completed / SELLER_STAGES.length) * 100);
  return (
    <Link
      to={`/seller/company/${co.company_id}`}
      className="hairline border-l-0 border-t-0 p-7 bg-card hover:bg-bone transition-colors block"
      data-testid={`seller-company-card-${co.company_id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <span className="font-mono-label text-gold">{co.sector} · {co.country}</span>
          <h3 className="font-serif-display text-2xl mt-1">{co.name}</h3>
        </div>
        <div className="flex flex-col gap-1 items-end">
          {co.qualified ? (
            <span className="font-mono-label bg-forest/10 text-forest border border-forest/30 px-2 py-1">{t("Cualificada")}</span>
          ) : (
            <span className="font-mono-label bg-secondary px-2 py-1">{t("En desarrollo")}</span>
          )}
          {co.teaser_published && (
            <span className="font-mono-label bg-gold/15 text-[#A16207] border border-gold/40 px-2 py-1">{t("Teaser publicado")}</span>
          )}
          {co.im_published && (
            <span className="font-mono-label bg-[#1E3A5F]/5 text-[#1E3A5F] border border-[#1E3A5F]/30 px-2 py-1">{t("IM publicado")}</span>
          )}
        </div>
      </div>
      <div className="space-y-2 mb-5">
        <div className="flex justify-between font-mono-label">
          <span>{t("Progreso ·")}{" "}{completed}/{SELLER_STAGES.length}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1 bg-secondary relative">
          <div className="absolute inset-y-0 left-0 bg-[#1E3A5F]" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{t("Etapa actual:")}{" "}<span className="text-foreground font-medium">{stage.name}</span></span>
        <ArrowRight className="w-4 h-4" />
      </div>
      {co.score != null && (
        <div className="mt-4 hairline-t pt-4 flex items-center justify-between">
          <span className="font-mono-label">{t("Score IA")}</span>
          <span className="font-serif-display text-2xl text-gold">{co.score}<span className="text-xs text-muted-foreground">/100</span></span>
        </div>
      )}
    </Link>
  );
};
